/* eslint-disable @typescript-eslint/ban-ts-comment */
import cloneDeep from 'lodash/cloneDeep';
import React, { createContext, Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IBuildingProviderContext, IProviderProps } from '../../@types/ContextProviderRelated';
import { EUnitType, EWall } from '../../@types/Enums';
import { IFilesApiObject } from '../../@types/FilesAPIModels';
import FetchingInfoModal from '../../components/modals/FetchingInfoModal';
import FilesAPIDownModal from '../../components/modals/FilesAPIDownModal';
import GLTFImportNotSupportedModal from '../../components/modals/GLTFImportNotSupportedModal';
import PanoramaMeasurementSelectionModal from '../../components/modals/PanoramaMeasurementSelectionModal';
import RegionConflictModal from '../../components/modals/RegionConflictModal';
import { usePlanLoader } from '../../hooks/usePlanLoader';
import useUndoRedo, { EHistoryAction, IHistoryEntry } from '../../hooks/useUndoRedo';
import { IWall } from '../../models/abstract/Wall';
import CameraPositionSpriteModel from '../../models/standard/CameraPositionSpriteModel';
import CompassModel from '../../models/standard/CompassModel';
import ElementModel, { IElement } from '../../models/standard/ElementModel';
import FloorModel from '../../models/standard/FloorModel';
import HotspotModel from '../../models/standard/HotspotModel';
import Model2DModel, { IModel2D } from '../../models/standard/Model2DModel';
import PanoMeasurementModel from '../../models/standard/PanoMeasurementModel';
import PhysicalBoundaryModel, { BoundaryEdge } from '../../models/standard/PhysicalBoundary';
import { IPartitionWall } from '../../models/standard/PartitionWallModel';
import PlanModel from '../../models/standard/PlanModel';
import ProductModel, { IProduct } from '../../models/standard/ProductModel';
import RoomModel from '../../models/standard/RoomModel';
import SimpleMeasurementModel from '../../models/standard/SimpleMeasurementModel';
import { IStandardWall } from '../../models/standard/StandardWallModel';
import { fetchAttributesForObject, updateWallGeometryAttributes } from '../../utils/attributes/attributesForObjectLoader';
import { DEFAULT_OBJECT_IDS, ObjectType } from '../../utils/attributes/defaultObjectsAndAttributes';
import exportPlanJson from '../../utils/export/exportPlanJson';
import { hasPendingExternalPlanSession } from '../../utils/externalPlanDataStorage';
import filesApiService from '../../utils/filesApiService';
import { LAST_SAVE_THRESHOLD_MS, loadWithExpiration, LOCAL_STORAGE_KEY, saveWithExpiration } from '../../utils/localStorageUtils';
import { importPlanJson } from '../../utils/planJsonExportImportUtils';
import { COUNTRY_TO_REGION, ERegionCode } from '../../utils/regionUtils';
import { snapWallElementsToWalls } from '../../utils/snapWallElementsToWalls';
import { getSpaceUuidAtPointByGeometry } from '../../utils/spaceUtils';
import { validateClosedRooms } from '../../utils/vectorUtils';
import { useAnnotations } from '../AnnotationsProvider';
import { useDrawing } from '../DrawingProvider';
import { useHistory } from '../HistoryProvider';
import { useLoadingContext } from '../LoadingProvider';
import { PendingItemProvider } from '../PendingItemProvider';
import { usePlan } from '../PlanProvider';
import { useQueryParams } from '../QueryParamsProvider';
import { useSelection } from '../SelectionProvider';
import { useUserDataContext } from '../UserDataProvider';
import { IBuildingProviderActions } from './actions/BuildingProviderActions.type';
import { useDoorActions } from './actions/useDoorActions';
import { useElementActions } from './actions/useElementActions';
import { useFloorActions } from './actions/useFloorActions';
import { useModel2DActions } from './actions/useModel2DActions';
import { useMultiRoomActions } from './actions/useMultiRoomActions';
import { useOverallActions } from './actions/useOverallActions';
import { usePanoramaMeasurementActions } from './actions/usePanoramaMeasurementActions';
import { usePassageActions } from './actions/usePassageActions';
import { useProductActions } from './actions/useProductActions';
import { useRadiatorActions } from './actions/useRadiatorActions';
import { useRoomActions } from './actions/useRoomActions';
import { useTextureActions } from './actions/useTextureActions';
import { useTileActions } from './actions/useTileActions';
import { useWallActions } from './actions/useWallActions';
import { useWindowActions } from './actions/useWindowActions';

interface IContext {
  building: IBuildingProviderContext;
  allElements: IElement[];
  allProducts: IProduct[];
  allModels2D: IModel2D[];
  standardWalls: IStandardWall[];
  partitionWalls: IPartitionWall[];
  defaultObjects: IFilesApiObject[];
  currentLevel: number;
  hasBasement: boolean;
  upperFloorAmount: number;
  roomPerimeter: number;
  setBuilding: Dispatch<SetStateAction<IBuildingProviderContext>>;
  deletionConfirmed: boolean;
}

// Context Export
export type IBuildingContext = IContext & IBuildingProviderActions;
export const BuildingContext = createContext<IBuildingContext | undefined>(undefined);

const collectRoomWallUuids = (room: RoomModel): Set<string> => {
  const wallUuids = new Set<string>();

  (room.spaces ?? []).forEach((space) => {
    (space.boundaries ?? []).forEach((boundary) => {
      if (!(boundary instanceof PhysicalBoundaryModel)) return;

      (boundary.edges ?? []).forEach((edge) => {
        if (edge?.wall?.uuid) wallUuids.add(edge.wall.uuid);
      });

      (boundary.walls ?? []).forEach((wall) => {
        if (wall?.uuid) wallUuids.add(wall.uuid);
      });
    });

    (space.walls ?? []).forEach((wall) => {
      if (wall?.uuid) wallUuids.add(wall.uuid);
    });
  });

  return wallUuids;
};

const collectRoomWalls = (room: RoomModel): IWall[] => {
  const wallsByUuid = new Map<string, IWall>();

  (room.spaces ?? []).forEach((space) => {
    (space.boundaries ?? []).forEach((boundary) => {
      if (!(boundary instanceof PhysicalBoundaryModel)) return;

      (boundary.edges ?? []).forEach((edge) => {
        if (edge?.wall?.uuid) wallsByUuid.set(edge.wall.uuid, edge.wall);
      });

      (boundary.walls ?? []).forEach((wall) => {
        if (wall?.uuid) wallsByUuid.set(wall.uuid, wall);
      });
    });

    (space.walls ?? []).forEach((wall) => {
      if (wall?.uuid) wallsByUuid.set(wall.uuid, wall);
    });
  });

  return Array.from(wallsByUuid.values());
};

const collectRoomSpaceUuids = (rooms: RoomModel[]): Set<string> => {
  const spaceUuids = new Set<string>();

  rooms.forEach((room) => {
    (room.spaces ?? []).forEach((space) => {
      if (space?.uuid) spaceUuids.add(space.uuid);
    });
  });

  return spaceUuids;
};

const cloneCameraPositionSpritesForBuilding = (
  building: IBuildingProviderContext,
  cameraPositionSprites: CameraPositionSpriteModel[] = [],
): CameraPositionSpriteModel[] => {
  return cameraPositionSprites.map((sprite) => {
    const clonedSprite = cloneDeep(sprite);

    if (!clonedSprite.spaceUuid && clonedSprite.position) {
      clonedSprite.spaceUuid = getSpaceUuidAtPointByGeometry(building, clonedSprite.position);
    }

    return clonedSprite;
  });
};

const filterCameraPositionSpritesForRooms = (
  cameraPositionSprites: CameraPositionSpriteModel[],
  rooms: RoomModel[],
  includeUnresolved: boolean,
): CameraPositionSpriteModel[] => {
  const includedSpaceUuids = collectRoomSpaceUuids(rooms);

  return cameraPositionSprites.filter((sprite) => {
    if (!sprite.spaceUuid) return includeUnresolved;
    return includedSpaceUuids.has(sprite.spaceUuid);
  });
};

interface PersistPlanSnapshotOptions {
  building?: IBuildingProviderContext;
  cameraPositionSprites?: CameraPositionSpriteModel[];
  force?: boolean;
  hotspots?: HotspotModel[];
  measurements?: SimpleMeasurementModel[];
  plan?: PlanModel;
}

// Hook definition
export const useBuilding = () => {
  const context = useContext(BuildingContext);
  if (!context) {
    throw new Error('useBuilding must be used within a BuildingProvider');
  }
  return context;
};

export const BuildingProvider = ({ children }: IProviderProps): JSX.Element => {
  // Hooks
  const { registerBuildingChange, resetHistory: resetBuildingHistory } = useUndoRedo();
  const { setHistory, setCurrentIndex, history } = useHistory();
  const { setIsLoading } = useLoadingContext();
  const [isPlanLoaderBusy, setIsPlanLoaderBusy] = useState(false);
  const [pendingDoor, setPendingDoor] = useState<{
    wallIndex: number;
    doorIndex: number;
  } | null>(null);

  const [pendingHistory, setPendingHistory] = useState<IBuildingProviderContext | null>(null);

  // States
  const [building, _setBuilding] = useState<IBuildingProviderContext>({
    compass: new CompassModel(),
    walls: [],
    products: [],
    elements: [],
    models2D: [],
    isSetByUndoRedo: false,
    lastAction: EHistoryAction.UNKNOWN,
    nonFetchableEntities: [],
    relations: [],
    rooms: [],
  });

  const initializeImportedBuildingHistory = useCallback(
    (importedBuilding: IBuildingProviderContext, cameraPositionSprites: CameraPositionSpriteModel[] = []): IBuildingProviderContext => {
      resetBuildingHistory();

      const snappedImportedBuilding = snapWallElementsToWalls(importedBuilding);
      const resolvedCameraPositionSprites = cloneCameraPositionSpritesForBuilding(snappedImportedBuilding, cameraPositionSprites);
      const importedRooms = snappedImportedBuilding.rooms ?? [];
      const timestamp = Date.now();
      const importHistory: IHistoryEntry[] = [];
      const importedWallByUuid = new Map<string, IWall>();

      (snappedImportedBuilding.walls ?? []).forEach((wall) => {
        if (wall?.uuid) importedWallByUuid.set(wall.uuid, wall as IWall);
      });

      importedRooms.forEach((room) => {
        collectRoomWalls(room as RoomModel).forEach((wall) => {
          if (wall?.uuid && !importedWallByUuid.has(wall.uuid)) {
            importedWallByUuid.set(wall.uuid, wall);
          }
        });
      });

      if (importedRooms.length === 0) {
        importHistory.push({
          building: cloneDeep({ ...snappedImportedBuilding, isSetByUndoRedo: true }),
          cameraPositionSprites: cloneDeep(resolvedCameraPositionSprites),
          roomCompleted: true,
          timestamp,
          action: EHistoryAction.BUILDING_CREATED,
        });
      }

      for (let roomIndex = 0; roomIndex < importedRooms.length; roomIndex++) {
        const includedRooms = importedRooms.slice(0, roomIndex + 1) as RoomModel[];
        const includedWallUuids = new Set<string>();

        includedRooms.forEach((room) => {
          collectRoomWallUuids(room).forEach((uuid) => includedWallUuids.add(uuid));
        });

        const includedWalls = Array.from(includedWallUuids)
          .map((uuid) => importedWallByUuid.get(uuid))
          .filter((wall): wall is IWall => wall !== undefined);
        const includedCameraPositionSprites = filterCameraPositionSpritesForRooms(
          resolvedCameraPositionSprites,
          includedRooms,
          roomIndex === importedRooms.length - 1,
        );

        importHistory.push({
          building: cloneDeep({
            ...snappedImportedBuilding,
            rooms: includedRooms,
            walls: includedWalls,
            isSetByUndoRedo: true,
          }),
          cameraPositionSprites: cloneDeep(includedCameraPositionSprites),
          roomCompleted: true,
          timestamp: timestamp + roomIndex,
          action: roomIndex === 0 ? EHistoryAction.BUILDING_CREATED : EHistoryAction.ROOM_CREATED,
        });
      }

      setHistory(importHistory);
      setCurrentIndex(importHistory.length - 1);
      return snappedImportedBuilding;
    },
    [resetBuildingHistory, setHistory, setCurrentIndex],
  );

  const safeUuid = (u: any): string => (typeof u === 'string' ? u : u !== null ? String(u) : '');

  // --- 1. FLATTEN & SORT PRODUCTS ---
  const allProducts = useMemo(() => {
    const map = new Map<string, IProduct>();

    (building.products || []).forEach((p) => {
      const id = safeUuid(p.uuid);
      if (id) map.set(id, p);
    });

    building.rooms?.forEach((room) => {
      room.spaces?.forEach((space) => {
        space.products?.forEach((p) => {
          const id = safeUuid(p.uuid);
          if (id) map.set(id, p);
        });
      });
    });

    return Array.from(map.values()).sort((a, b) => safeUuid(a.uuid).localeCompare(safeUuid(b.uuid)));
  }, [building.products, building.rooms]);

  // --- 2. FLATTEN & SORT MODELS 2D ---
  const allModels2D = useMemo(() => {
    const map = new Map<string, IModel2D>();

    (building.models2D || []).forEach((m) => {
      const id = safeUuid(m.uuid);
      if (id) map.set(id, m);
    });

    building.rooms?.forEach((room) => {
      room.spaces?.forEach((space) => {
        space.models2D?.forEach((m) => {
          const id = safeUuid(m.uuid);
          if (id) map.set(id, m);
        });
      });
    });

    return Array.from(map.values()).sort((a, b) => safeUuid(a.uuid).localeCompare(safeUuid(b.uuid)));
  }, [building.models2D, building.rooms]);

  // Calculate Aggregated Elements (Single Source of Truth for rendering)
  const allElements = useMemo(() => {
    const map = new Map<string, IElement>();

    (building.elements || []).forEach((el) => {
      const id = safeUuid(el.uuid);
      if (id) map.set(id, el);
    });

    building.rooms?.forEach((room) => {
      room.spaces?.forEach((space: any) => {
        space.elements?.forEach((el: IElement) => {
          const id = safeUuid(el.uuid);
          if (id) map.set(id, el);
        });
      });
    });

    return Array.from(map.values()).sort((a, b) => safeUuid(a.uuid).localeCompare(safeUuid(b.uuid)));
  }, [building.elements, building.rooms]);

  // Fetching related
  const [deletionConfirmed, setDeletionConfirmed] = useState<boolean>(false);
  const [showFetchingInfoModal, setShowFetchingInfoModal] = useState<boolean>(false);
  const [showFilesApiDownModal, setShowFilesApiDownModal] = useState<boolean>(false);
  const [isStorageLoading, setIsStorageLoading] = useState(false);

  // @ts-ignore
  const { planModel, setPlanModel: setPlanModelState } = usePlan();
  const queryParams = useQueryParams();
  const { hasSelectionWithoutDrawer } = useSelection();
  const { currentUserData, setCurrentUserData } = useUserDataContext();
  const { roomCompleted, setRoomCompleted } = useDrawing();
  const [showImportNotSupportedModal, setShowImportNotSupportedModal] = useState(false);
  const { hotspots, measurements, cameraPositionSprites, addCameraPositionSprite, resetAnnotations, addMeasurement, addHotspot } = useAnnotations();
  const [isUserLoggedIn, setIsUserLoggedIn] = useState<boolean>(false);
  const planModelRef = useRef(planModel);
  const buildingRef = useRef(building);
  const measurementsRef = useRef(measurements);
  const cameraPositionSpritesRef = useRef(cameraPositionSprites);
  const hotspotRef = useRef(hotspots);
  const roomCompletedRef = useRef(roomCompleted);
  const navigate = useNavigate();
  const lastSaveTimeRef = useRef(0);
  const [defaultObjects, setDefaultObjects] = useState<IFilesApiObject[]>([]);

  /** tracks attribute fetches to avoid duplicate/infinite loops */
  const attrFetchInFlightRef = useRef<Set<string>>(new Set()); // key = `${ObjectType}:${uuid}`

  const setPlanModel = useCallback(
    (nextPlan: React.SetStateAction<PlanModel>) => {
      if (typeof nextPlan === 'function') {
        setPlanModelState((previousPlan) => {
          const resolvedPlan = (nextPlan as (previousPlan: PlanModel) => PlanModel)(previousPlan);
          planModelRef.current = resolvedPlan;
          return resolvedPlan;
        });
        return;
      }

      planModelRef.current = nextPlan;
      setPlanModelState(nextPlan);
    },
    [setPlanModelState],
  );

  useEffect(() => {
    planModelRef.current = planModel;
  }, [planModel]);

  useEffect(() => {
    buildingRef.current = building;
  }, [building]);

  useEffect(() => {
    measurementsRef.current = measurements;
  }, [measurements]);

  useEffect(() => {
    hotspotRef.current = hotspots;
  }, [hotspots]);

  useEffect(() => {
    cameraPositionSpritesRef.current = cameraPositionSprites;
  }, [cameraPositionSprites]);

  useEffect(() => {
    roomCompletedRef.current = roomCompleted;
  }, [roomCompleted]);

  const persistPlanSnapshot = useCallback((options: PersistPlanSnapshotOptions = {}): boolean => {
    const snapshotBuilding = options.building ?? buildingRef.current;
    const isValidTopology = validateClosedRooms(snapshotBuilding.walls || [], true);
    const hasCompletedRooms =
      snapshotBuilding.rooms &&
      snapshotBuilding.rooms.length > 0 &&
      snapshotBuilding.rooms[0].spaces &&
      snapshotBuilding.rooms[0].spaces.length > 0;

    if (!isValidTopology || !hasCompletedRooms) {
      return false;
    }

    const now = Date.now();
    if (!options.force && now - lastSaveTimeRef.current < LAST_SAVE_THRESHOLD_MS) {
      return false;
    }

    try {
      const planJson = exportPlanJson(
        options.plan ?? planModelRef.current,
        snapshotBuilding,
        options.measurements ?? measurementsRef.current,
        options.cameraPositionSprites ?? cameraPositionSpritesRef.current,
        options.hotspots ?? hotspotRef.current,
        snapshotBuilding.nonFetchableEntities,
        snapshotBuilding.relations,
      );
      saveWithExpiration(LOCAL_STORAGE_KEY, planJson);
      lastSaveTimeRef.current = now;
      return true;
    } catch (error) {
      console.error('Auto-save failed:', error);
      return false;
    }
  }, []);

  const cleanSceneBeforeImport = useCallback(() => {
    resetAnnotations();
  }, [resetAnnotations]);

  useEffect(() => {
    const fetchDefaultObjects = async () => {
      try {
        const regionCode = planModel?.region ? String(planModel.region).toUpperCase() : 'DE';
        const fetchedObjects = await Promise.allSettled(DEFAULT_OBJECT_IDS.map((id) => filesApiService.getObjectById(id, regionCode)));
        const defaultsOnly = fetchedObjects
          .filter((result): result is PromiseFulfilledResult<IFilesApiObject | null> => result.status === 'fulfilled')
          .map((result) => result.value)
          .filter((object): object is IFilesApiObject => object !== null);

        setDefaultObjects(defaultsOnly);
      } catch (error) {
        console.error(error);
      }
    };

    fetchDefaultObjects();
  }, [planModel?.region]);

  const setBuilding = useCallback((newBuilding: IBuildingProviderContext | ((prev: IBuildingProviderContext) => IBuildingProviderContext), forceUpdate?: boolean) => {
    _setBuilding((prev) => {
      const updatedBuilding = typeof newBuilding === 'function' ? newBuilding(prev) : newBuilding;

      // --- 1. VALIDATION CHECK ---
      // Check if the new state forms a valid closed room structure.
      // We use strict=true if you want to ensure NO dangling walls exist in the save state.
      // Or strict=false if you just want to ensure *at least one* room exists.
      const isValidTopology = validateClosedRooms(updatedBuilding.walls || [], true); // true = Strict Mode
      buildingRef.current = updatedBuilding;

      // --- 2. AUTO-SAVE (Local Storage) ---
      // Only save to disk if valid AND time threshold passed
      persistPlanSnapshot({ building: updatedBuilding, force: forceUpdate });

      // --- 3. HISTORY (Undo/Redo) ---
      // CRITICAL: Only push to undo stack if the building is geometrically valid!
      // This prevents the user from "Undoing" back into a broken/ripped state.
      if (isValidTopology || forceUpdate) {
        setPendingHistory(updatedBuilding);
      }
      return updatedBuilding;
    });
  }, [persistPlanSnapshot]);

  useEffect(() => {
    if (pendingHistory && pendingHistory.lastAction) {
      registerBuildingChange(pendingHistory, pendingHistory.lastAction);
      setPendingHistory(null);
    }
  }, [pendingHistory]);

  // Hear to initial creation
  useEffect(() => {
    if (history.length === 0) {
      registerBuildingChange(building, building.lastAction);
    }
  }, [history, building.walls]);

  const panoramaMeasurementActions = usePanoramaMeasurementActions(building, setBuilding, setShowFetchingInfoModal, initializeImportedBuildingHistory);

  const { authLoading } = usePlanLoader({
    building,
    setPlanModel,
    setBuilding,
    setRoomCompleted,
    initializeImportedBuildingHistory,
    panoramaMeasurementActions,
    addMeasurement,
    addCameraPositionSprite,
    addHotspot,
    resetAnnotations,
    setShowImportNotSupportedModal,
    setShowFetchingInfoModal,
    setIsPlanLoaderBusy,
  });

  useEffect(() => {
    if (currentUserData.authentificationtoken !== '') {
      setIsUserLoggedIn(true);
    } else {
      setIsUserLoggedIn(false);
    }
  }, [currentUserData, currentUserData.authentificationtoken]);

  useEffect(() => {
    const loadPlanDataFromStorage = async () => {
      setIsStorageLoading(true);
      try {
        const hasQueryParams = queryParams.data !== undefined;
        const hasExternalPlanSession = hasPendingExternalPlanSession();

        if (hasQueryParams || hasExternalPlanSession) {
          // Clear localStorage to prevent using old data
          localStorage.removeItem(LOCAL_STORAGE_KEY);
          console.warn('[PlanDebug] skipped localStorage plan restore because external plan data is pending', {
            hasExternalPlanSession,
            hasQueryParams,
          });
        } else if (!authLoading) {
          const activePlan = planModelRef.current;
          const hasActivePlan =
            activePlan.id !== -1 ||
            activePlan.kojeId !== -1 ||
            activePlan.kojeLevel !== -1 ||
            activePlan.eventId !== -1 ||
            activePlan.dataObjectId !== -1 ||
            activePlan.showroomId !== -1;

          if (hasActivePlan) {
            console.warn('[PlanDebug] skipped localStorage plan restore because active plan already exists', {
              dataObjectId: activePlan.dataObjectId,
              eventId: activePlan.eventId,
              floorplannerId: activePlan.id,
              kojeId: activePlan.kojeId,
              kojeLevel: activePlan.kojeLevel,
              showroomId: activePlan.showroomId,
            });
            return;
          }

          // Load from localStorage only if no parameters are present
          const savedPlan = loadWithExpiration<string>(LOCAL_STORAGE_KEY);
          if (savedPlan) {
            // Log in to files API before loading the rest of the plan
            await filesApiService.ensureAuthenticated();
            // Flag for the status of the files api
            const isFilesApiAvailable = await filesApiService.checkFilesApiStatus();

            const {
              plan,
              building: importedBuilding,
              measurements,
              cameraPositionSprites: importedCameraPositionSprites,
              hotspots,
              nonFetchableEntities,
            } = await importPlanJson(savedPlan, isFilesApiAvailable);
            console.warn('[PlanDebug] restoring plan from localStorage', {
              dataObjectId: plan.dataObjectId,
              eventId: plan.eventId,
              floorplannerId: plan.id,
              kojeId: plan.kojeId,
              kojeLevel: plan.kojeLevel,
              showroomId: plan.showroomId,
            });
            const restoredBuilding = snapWallElementsToWalls(importedBuilding);
            const restoredCameraPositionSprites = cloneCameraPositionSpritesForBuilding(restoredBuilding, importedCameraPositionSprites);
            restoredBuilding.nonFetchableEntities = nonFetchableEntities;
            const buildingCopy = cloneDeep(restoredBuilding);
            buildingCopy.isSetByUndoRedo = true;
            const history: Array<IHistoryEntry> = [];
            history.push({
              building: buildingCopy,
              cameraPositionSprites: cloneDeep(restoredCameraPositionSprites),
              roomCompleted: true,
              timestamp: Date.now(),
              action: EHistoryAction.BUILDING_CREATED,
            });
            setHistory(history);
            setCurrentIndex(0);
            setPlanModel(plan);
            buildingRef.current = restoredBuilding;
            _setBuilding(restoredBuilding);
            resetAnnotations();
            measurements.forEach((m) => addMeasurement(m));
            restoredCameraPositionSprites.forEach((m) => addCameraPositionSprite(m));
            hotspots.forEach((h) => addHotspot(h));
            persistPlanSnapshot({
              plan,
              building: restoredBuilding,
              measurements,
              cameraPositionSprites: restoredCameraPositionSprites,
              hotspots,
              force: true,
            });
            setRoomCompleted(true);
            const hasProjectIds = plan && plan.kojeId !== -1 && plan.eventId !== -1;
            const hasKojeLevelIds = plan && plan.kojeId !== -1 && plan.kojeLevel !== -1;
            if ((hasProjectIds || hasKojeLevelIds) && currentUserData.authentificationtoken !== '') {
              const newModels = (await panoramaMeasurementActions.fetchPanoramaMeasurements(plan, currentUserData.authentificationtoken)) as Array<PanoMeasurementModel>;
            }

            // Case: File Api is down
            if (!isFilesApiAvailable) {
              setShowFilesApiDownModal(true);
            }

            // Case: Some building objects could not be fetched
            if (isFilesApiAvailable && nonFetchableEntities.length > 0) {
              setShowFetchingInfoModal(true);
            }
          }
        }
      } finally {
        setIsStorageLoading(false);
      }
    };
    loadPlanDataFromStorage();
  }, [queryParams, authLoading]);

  useEffect(() => {
    setIsLoading(authLoading || isStorageLoading || isPlanLoaderBusy || panoramaMeasurementActions.measurementLoading);
  }, [authLoading, isStorageLoading, isPlanLoaderBusy, panoramaMeasurementActions.measurementLoading, setIsLoading]);

  useEffect(() => {
    if (authLoading || isStorageLoading || isPlanLoaderBusy || panoramaMeasurementActions.measurementLoading) return;

    persistPlanSnapshot({
      measurements,
      cameraPositionSprites,
      hotspots,
      force: true,
    });
  }, [
    authLoading,
    cameraPositionSprites,
    hotspots,
    isPlanLoaderBusy,
    isStorageLoading,
    measurements,
    panoramaMeasurementActions.measurementLoading,
    persistPlanSnapshot,
  ]);

  useEffect(() => {
    // wait for all the heavy lifting to finish loading
    if (authLoading || isStorageLoading || isPlanLoaderBusy || panoramaMeasurementActions.measurementLoading) return;

    // grab the current plan from ref to avoid stale state issues
    const currentPlan = planModelRef.current;

    // only proceed if we don't have a region set yet
    if (!currentPlan.region || currentPlan.region === '') {
      const hasToken = !!currentUserData.authentificationtoken;

      // guard clause: if we have a token, we must ensure the profile is actually loaded before proceeding.
      // if we don't have a token, we skip this and fall back to the default immediately.
      if (hasToken) {
        const isProfileLoaded = !!currentUserData.id && currentUserData.id !== '';
        if (!isProfileLoaded) {
          // token exists, but profile pending -> bail and wait for next render
          return;
        }
      }

      // console.log('Setting initial region (BuildingProvider) - current plan region is empty:', currentPlan);
      // console.log('Setting initial region (BuildingProvider) - current plan region is empty:', currentUserData);
      let determinedRegion: string = ERegionCode.DE; // default fallback

      // if we have a user logged in, try to map their country to a region
      if (hasToken && currentUserData.country) {
        const userRegion = COUNTRY_TO_REGION[currentUserData.country];
        if (userRegion) {
          determinedRegion = userRegion;
        }
      }

      console.warn('[PlanDebug] applying default region to plan', {
        determinedRegion,
        currentPlan: {
          dataObjectId: currentPlan.dataObjectId,
          eventId: currentPlan.eventId,
          floorplannerId: currentPlan.id,
          kojeId: currentPlan.kojeId,
          kojeLevel: currentPlan.kojeLevel,
          showroomId: currentPlan.showroomId,
        },
      });

      // Use the latest React state in the setter to avoid losing IDs when this effect races
      // against the external plan loader.
      setPlanModel((previousPlan) => {
        if (previousPlan.region && previousPlan.region !== '') {
          return previousPlan;
        }

        return new PlanModel(
          previousPlan.name,
          previousPlan.id,
          previousPlan.kojeId,
          previousPlan.kojeLevel,
          previousPlan.eventId,
          previousPlan.dataObjectId,
          previousPlan.showroomId,
          previousPlan.appVersion,
          previousPlan.building,
          previousPlan.jsonVersion,
          determinedRegion,
        );
      });
    }
  }, [authLoading, isStorageLoading, isPlanLoaderBusy, panoramaMeasurementActions.measurementLoading, currentUserData, setPlanModel]);

  useEffect(() => {
    if (deletionConfirmed === true) {
      const nonFetchableEntities = building.nonFetchableEntities;

      // Collect IDs of all non-fetchable objects to delete
      const objectToDelete = nonFetchableEntities.map((object) => String(object.id));

      // Remove texture from floor if it's non-fetchable
      const legacyFloor: FloorModel | undefined = (building as any)?.floor;
      let newFloor = legacyFloor;
      if (legacyFloor?.texture && objectToDelete.includes(String(legacyFloor.texture.id))) {
        newFloor = new FloorModel(legacyFloor.tiles, null, legacyFloor.attributes, legacyFloor.uuid);
      }

      // Filter out products that should be deleted
      let newProducts = building.products.filter((product) => !objectToDelete.includes(String(product.id)));

      // Remove textures from products if their textures are non-fetchable
      newProducts = newProducts.map((product) => {
        if (product.texture && objectToDelete.includes(String(product.texture.id))) {
          return new ProductModel(
            product.id,
            product.name,
            product.svgPath,
            product.dimensions.length,
            product.dimensions.height ?? 0,
            product.dimensions.depth,
            product.position,
            product.rotation,
            product.fillColor,
            product.strokeColor,
            product.strokeWidth,
            null, // texture removed
            product.attributes,
            product.objects,
            product.description,
            product.manufacturer,
          );
        }
        return product;
      });

      // Remove textures from elements if their textures are non-fetchable
      const newElements = building.elements.map((element) => {
        if (element.texture && objectToDelete.includes(String(element.texture.id))) {
          return new ElementModel(
            element.type,
            element.length,
            element.width,
            element.position,
            element.rotation,
            null, // texture removed
            element.description,
            element.useAsTextField,
            element.fontSize,
            element.object,
            element.attributes,
            element.sideC,
            element.triangleAngle,
          );
        }
        return element;
      });

      // Filter out 2D models that should be deleted
      let newModels2D = building.models2D.filter((model) => !objectToDelete.includes(String(model.id)));

      // Remove textures from 2D models if their textures are non-fetchable
      newModels2D = newModels2D.map((model) => {
        if (model.texture && objectToDelete.includes(String(model.texture.id))) {
          return new Model2DModel(
            model.id,
            model.name,
            model.filePath,
            model.fileFormat,
            model.fileSize,
            model.dimensions.length,
            model.dimensions.depth,
            model.object,
            model.position,
            model.rotation,
            null, // texture removed
          );
        }
        return model;
      });

      // Create new building object without non-fetchable objects
      const newBuilding: IBuildingProviderContext = {
        ...building,
        // @ts-ignore (legacy: keep if present)
        floor: newFloor,
        products: newProducts,
        elements: newElements,
        models2D: newModels2D,
        nonFetchableEntities: [], // clear non-fetchable list
      };

      setBuilding(newBuilding, true);
      setDeletionConfirmed(false);
    }
  }, [deletionConfirmed]);

  const [currentLevel, setCurrentLevel] = useState<number>(0);
  const [hasBasement, setHasBasement] = useState<boolean>(false);
  const [upperFloorAmount, setUpperFloorAmount] = useState<number>(0);
  const [roomPerimeter, setRoomPerimeter] = useState<number>(0);

  // State based value abstractions
  const standardWalls = useMemo(() => building.walls.filter((wall) => wall.type === EWall.STANDARD), [building.walls]);
  const partitionWalls = useMemo(() => building.walls.filter((wall) => wall.type === EWall.PARTITION), [building.walls]);

  // Domain-specific hooks
  const wallActions = useWallActions(setBuilding);
  const floorActions = useFloorActions(setBuilding);
  const doorActions = useDoorActions(setBuilding);
  const windowActions = useWindowActions(setBuilding);
  const passageActions = usePassageActions(setBuilding);
  const tileActions = useTileActions(setBuilding);
  const roomActions = useRoomActions(setBuilding);
  const productActions = useProductActions(setBuilding);
  const radiatorActions = useRadiatorActions(setBuilding);
  const elementActions = useElementActions(setBuilding);
  const model2DActions = useModel2DActions(setBuilding);
  const overallActions = useOverallActions(building, setBuilding, planModel);
  const textureActions = useTextureActions(setBuilding);
  const multiRoomActions = useMultiRoomActions(setBuilding);

  // ---------- NEW: default attribute bootstrapper (rooms + floors + walls) ----------
  useEffect(() => {
    // collect targets with missing attributes
    type Target = { type: ObjectType; uuid: string; model: any };
    const targets: Target[] = [];
    const queuedTargets = new Set<string>();

    const addTarget = (type: ObjectType, uuid: string | undefined, model: any) => {
      if (!uuid) return;

      const key = `${type}:${uuid}`;
      if (queuedTargets.has(key)) return;

      queuedTargets.add(key);
      targets.push({ type, uuid, model });
    };

    const needsAttributes = (model: any): boolean => !Array.isArray(model?.attributes) || model.attributes.length === 0;

    (building.rooms ?? []).forEach((room) => {
      if (needsAttributes(room)) {
        addTarget(ObjectType.Room, room?.uuid, room);
      }

      (room.spaces ?? []).forEach((space: any) => {
        // UPDATED: Check single floor 1:1
        const floor = space.floor as FloorModel | undefined;

        if (floor) {
          if (needsAttributes(floor)) {
            addTarget(ObjectType.Floor, (floor as any).uuid, floor);
          }
        }

        (space.walls ?? []).forEach((wall: any) => {
          if (needsAttributes(wall)) {
            addTarget(ObjectType.Wall, wall?.uuid, wall);
          }
        });
      });
    });

    (building.walls ?? []).forEach((wall: any) => {
      if (needsAttributes(wall)) {
        addTarget(ObjectType.Wall, wall?.uuid, wall);
      }
    });

    targets.forEach((t) => {
      const key = `${t.type}:${t.uuid}`;
      if (attrFetchInFlightRef.current.has(key)) return;
      attrFetchInFlightRef.current.add(key);

      (async () => {
        try {
          const attrs = await fetchAttributesForObject(t.model, t.type, planModelRef.current.region);
          if (!attrs || attrs.length === 0) return;

          setBuilding((prev) => {
            let changed = false;

            if (t.type === ObjectType.Room) {
              const nextRooms = (prev.rooms ?? []).map((r) => {
                if (r.uuid !== t.uuid) return r;
                const alreadyHas = Array.isArray((r as any).attributes) && (r as any).attributes.length > 0;
                if (alreadyHas) return r;
                const updated = new RoomModel(r.title ?? '', r.spaces, attrs);
                if ((r as any).uuid) updated.setUUID((r as any).uuid);
                changed = true;
                return updated;
              });
              return changed ? { ...prev, rooms: nextRooms, lastAction: EHistoryAction.ROOM_ATTRIBUES_UPDATED } : prev;
            }

            if (t.type === ObjectType.Floor) {
              const nextRooms = (prev.rooms ?? []).map((r) => {
                let roomChanged = false;

                const nextSpaces = (r.spaces ?? []).map((s: any) => {
                  const f = s.floor as FloorModel | undefined;

                  // Check if this space contains the floor we are updating
                  if (!f || (f as any).uuid !== t.uuid) {
                    return s;
                  }

                  const alreadyHas = Array.isArray((f as any).attributes) && (f as any).attributes.length > 0;
                  if (alreadyHas) return s;

                  // Update the floor
                  const fUuid = (f as any).uuid;
                  const updatedFloor = new FloorModel(f.tiles, f.texture, attrs, fUuid);

                  // Reconstruct the Space with the single updated floor
                  roomChanged = true;
                  const updatedSpace = new (s.constructor as any)(
                    updatedFloor, // Single floor arg
                    s.products,
                    s.elements,
                    s.models2D,
                    s.boundaries,
                    s.walls,
                  );

                  if (s.uuid) updatedSpace.setUUID(s.uuid);

                  return updatedSpace;
                });

                if (roomChanged) {
                  const updatedRoom = new RoomModel(r.title ?? '', nextSpaces, r.attributes ?? []);
                  if ((r as any).uuid) updatedRoom.setUUID((r as any).uuid);
                  changed = true;
                  return updatedRoom;
                }
                return r;
              });

              return changed ? { ...prev, rooms: nextRooms, lastAction: EHistoryAction.FLOOR_ATTRIBUTES_UPDATED } : prev;
            }

            if (t.type === ObjectType.Wall) {
              const replacementsByUuid = new Map<string, any>();

              const buildUpdatedWall = (wall: any) => {
                if (!wall || wall.uuid !== t.uuid) return wall;

                const alreadyHas = Array.isArray(wall.attributes) && wall.attributes.length > 0;
                if (alreadyHas) return wall;

                const existingReplacement = replacementsByUuid.get(wall.uuid);
                if (existingReplacement) return existingReplacement;

                const wallAttributes = updateWallGeometryAttributes(wall, EUnitType.METRIC, attrs);
                const updatedWall = Object.assign(Object.create(Object.getPrototypeOf(wall)), wall);
                updatedWall.attributes = wallAttributes;

                replacementsByUuid.set(wall.uuid, updatedWall);
                changed = true;
                return updatedWall;
              };

              const replaceBoundaryWallRefs = (boundary: any) => {
                const boundaryWalls = Array.isArray(boundary?.walls)
                  ? boundary.walls.map((wall: any) => replacementsByUuid.get(wall?.uuid) ?? wall)
                  : boundary?.walls;

                const boundaryEdges = Array.isArray(boundary?.edges)
                  ? boundary.edges.map((edge: any) => {
                      const replacementWall = replacementsByUuid.get(edge?.wall?.uuid);
                      if (!replacementWall) return edge;

                      const updatedEdge = new BoundaryEdge(replacementWall, edge.t0, edge.t1, edge.side, edge.reversed);
                      if (edge.uuid) updatedEdge.setUUID(edge.uuid);
                      return updatedEdge;
                    })
                  : boundary?.edges;

                const boundaryChanged = boundaryWalls !== boundary?.walls || boundaryEdges !== boundary?.edges;
                if (!boundaryChanged) return boundary;

                if (boundary instanceof PhysicalBoundaryModel) {
                  const updatedBoundary = new PhysicalBoundaryModel(boundaryWalls ?? [], boundaryEdges ?? []);
                  if (boundary.uuid) updatedBoundary.setUUID(boundary.uuid);
                  return updatedBoundary;
                }

                const updatedBoundary = Object.assign(Object.create(Object.getPrototypeOf(boundary)), boundary);
                updatedBoundary.walls = boundaryWalls;
                updatedBoundary.edges = boundaryEdges;
                return updatedBoundary;
              };

              const nextWalls = (prev.walls ?? []).map(buildUpdatedWall);
              const nextRooms = (prev.rooms ?? []).map((room) => {
                let roomChanged = false;

                const nextSpaces = (room.spaces ?? []).map((space: any) => {
                  const nextSpaceWalls = (space.walls ?? []).map(buildUpdatedWall);
                  const spaceWallsChanged = nextSpaceWalls.some((wall: any, index: number) => wall !== (space.walls ?? [])[index]);

                  if (!spaceWallsChanged) return space;

                  const nextBoundaries = (space.boundaries ?? []).map(replaceBoundaryWallRefs);
                  const updatedSpace = new (space.constructor as any)(
                    space.floor,
                    space.products,
                    space.elements,
                    space.models2D,
                    nextBoundaries,
                    nextSpaceWalls,
                  );

                  if (space.uuid) updatedSpace.setUUID(space.uuid);
                  roomChanged = true;
                  return updatedSpace;
                });

                if (!roomChanged) return room;

                const updatedRoom = new RoomModel(room.title ?? '', nextSpaces, room.attributes ?? []);
                if ((room as any).uuid) updatedRoom.setUUID((room as any).uuid);
                return updatedRoom;
              });

              return changed ? { ...prev, walls: nextWalls, rooms: nextRooms, lastAction: EHistoryAction.WALL_STANDARD_ATTRIBUTE_UPDATED } : prev;
            }

            return prev;
          });
        } catch (e) {
          console.error('Default attribute fetch failed', t.type, t.uuid, e);
        } finally {
          attrFetchInFlightRef.current.delete(key);
        }
      })();
    });
  }, [building.rooms, building.walls, setBuilding]);
  // ------------------------------------------------------------------------

  // Fetching-Modal Handling
  const handleCloseFetchingModal = useCallback(() => {
    setDeletionConfirmed(false);
    setShowFetchingInfoModal(false);
  }, []);

  const handleCloseFilesApiDownModal = useCallback(() => {
    setShowFilesApiDownModal(false);
  }, []);

  const handleConfirmFetchningModal = useCallback(() => {
    setDeletionConfirmed(true);
    setShowFetchingInfoModal(false);
  }, []);

  // --- FIX: SMART UPDATE BY UUID (With Logs) ---
  const smartUpdateElement = useCallback(
    (_ignoredIndex: number, newModel: IElement) => {
      const targetUuid = newModel.uuid;

      setBuilding((prev) => {
        const next = { ...prev };
        let found = false;

        // 1. Try updating in Root (Unassigned)
        if (next.elements) {
          const rootIndex = next.elements.findIndex((el) => el.uuid === targetUuid);
          if (rootIndex !== -1) {
            // Clone array to trigger React update
            const newElements = [...next.elements];
            newElements[rootIndex] = newModel;
            next.elements = newElements;
            found = true;
          }
        }

        // 2. If not in Root, look in Spaces
        if (!found && next.rooms) {
          next.rooms = next.rooms.map((room) => {
            if (!room.spaces) return room;

            let roomModified = false;
            const newSpaces = room.spaces.map((space) => {
              if (!space.elements) return space;

              const elIndex = space.elements.findIndex((el) => el.uuid === targetUuid);

              if (elIndex !== -1) {
                found = true;
                roomModified = true;

                // Clone elements array
                const newSpaceElements = [...space.elements];
                newSpaceElements[elIndex] = newModel;

                // Return updated space copy
                const updatedSpace = Object.assign(Object.create(Object.getPrototypeOf(space)), space);
                updatedSpace.elements = newSpaceElements;
                return updatedSpace;
              }
              return space;
            });

            if (roomModified) {
              // Return updated room copy
              const updatedRoom = Object.assign(Object.create(Object.getPrototypeOf(room)), room);
              updatedRoom.spaces = newSpaces;
              return updatedRoom;
            }
            return room;
          });
        }
        return { ...next, isSetByUndoRedo: false, lastAction: EHistoryAction.ELEMENT_UPDATED };
      });
    },
    [setBuilding],
  );

  const contextValue = {
    setBuilding,
    building,
    allProducts,
    allModels2D,
    allElements,
    standardWalls: standardWalls as IStandardWall[],
    partitionWalls: partitionWalls as IPartitionWall[],
    currentLevel,
    hasBasement,
    upperFloorAmount,
    roomPerimeter,
    deletionConfirmed,
    ...wallActions,
    ...floorActions,
    ...doorActions,
    ...windowActions,
    ...passageActions,
    ...tileActions,
    ...roomActions,
    ...productActions,
    ...radiatorActions,
    ...elementActions,
    updateElement: smartUpdateElement, // Override standard update
    ...model2DActions,
    ...overallActions,
    ...panoramaMeasurementActions,
    ...textureActions,
    ...multiRoomActions,
    setCurrentLevel,
    setHasBasement,
    setUpperFloorAmount,
    setRoomPerimeter,
    setDeletionConfirmed,
    setShowFetchingInfoModal,
    pendingDoor,
    setPendingDoor,
    defaultObjects,
  };

  return (
    <BuildingContext.Provider value={contextValue}>
      <PendingItemProvider>
        {children}
        <GLTFImportNotSupportedModal isOpen={showImportNotSupportedModal} onClose={() => setShowImportNotSupportedModal(false)} />
        {/* Render the pano measurements modal if more than one measurement exists */}
        {panoramaMeasurementActions.measurementModalOpen && panoramaMeasurementActions.panoMeasurements.length > 1 && (
          <PanoramaMeasurementSelectionModal
            open={panoramaMeasurementActions.measurementModalOpen}
            measurements={panoramaMeasurementActions.panoMeasurements}
            onImport={panoramaMeasurementActions.handleImportMeasurements}
            onClose={() => panoramaMeasurementActions.setMeasurementModalOpen(false)}
          />
        )}
        {panoramaMeasurementActions.conflictModalOpen && (
          <RegionConflictModal
            open={panoramaMeasurementActions.conflictModalOpen}
            planRegion={planModel?.region || 'DE'}
            conflictingMeasurements={panoramaMeasurementActions.conflictingMeasurements}
            onConfirm={panoramaMeasurementActions.handleConfirmConflict}
            onClose={panoramaMeasurementActions.handleCancelConflict}
          />
        )}
        {/** Files API Down */}
        <FilesAPIDownModal isOpen={showFilesApiDownModal} onClose={handleCloseFilesApiDownModal}></FilesAPIDownModal>

        {/** Fetching Info */}
        <FetchingInfoModal
          nonFetchableEntities={building.nonFetchableEntities}
          isOpen={showFetchingInfoModal}
          onClose={handleCloseFetchingModal}
          onConfirm={handleConfirmFetchningModal}
        />
      </PendingItemProvider>
    </BuildingContext.Provider>
  );
};

export default BuildingProvider;
